import { Edit, PowerSettingsNew, Replay } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Coverage } from "fhir/r5";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ScreenMobile } from "@/components/common/ScreenMobile";
import { ScheduleSliderDotsBalloon } from "@/components/schedule/ScheduleSliderDotsBalloon";
import { DashViewAllButton } from "@/pages/home/components/PatientDash/components/DashViewAllButton";
import {
  HealthInsuranceCardFace,
  HealthInsuranceCardLayout,
} from "./HealthInsuranceCardFace";
import {
  InsuranceCardsSegmentedTabs,
  InsuranceCardsTab,
  InsuranceCardsTabHelperText,
  INSURANCE_CARDS_TABS,
  splitCoveragesByStatus,
} from "./InsuranceCardsSegmentedTabs";

interface InsuranceCardsViewProps {
  coverages: Coverage[];
  onEdit: (coverage: Coverage) => void;
  onToggleStatus: (coverage: Coverage) => void;
}

function InsuranceCardItem({
  coverage,
  colorIndex,
  onEdit,
  onToggleStatus,
  layout,
}: {
  coverage: Coverage;
  colorIndex: number;
  onEdit: () => void;
  onToggleStatus: () => void;
  layout: HealthInsuranceCardLayout;
}) {
  const theme = useTheme();
  const status = coverage.status || "active";
  const isActive = status === "active";
  const isMobileLayout = layout === "mobile";

  const actionsBar = (
    <Stack
      direction={isMobileLayout ? "column" : "row"}
      spacing={1}
      alignItems={isMobileLayout ? "stretch" : "center"}
      justifyContent={isMobileLayout ? "stretch" : "space-between"}
      sx={{
        px: isMobileLayout ? 1.25 : 0,
        py: isMobileLayout ? 1.1 : 0,
        ...(isMobileLayout
          ? {
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: alpha(theme.palette.grey[500], 0.04),
            }
          : {}),
      }}
    >
      <Button
        fullWidth={isMobileLayout}
        variant="outlined"
        size="small"
        startIcon={<Edit sx={{ fontSize: 17 }} />}
        onClick={onEdit}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 2,
          minHeight: 42,
          px: 1.5,
          flex: isMobileLayout ? undefined : "0 1 auto",
        }}
      >
        Editar
      </Button>
      <Button
        fullWidth={isMobileLayout}
        variant={isActive ? "outlined" : "contained"}
        color={isActive ? "warning" : "success"}
        size="small"
        startIcon={
          isActive ? (
            <PowerSettingsNew sx={{ fontSize: 17 }} />
          ) : (
            <Replay sx={{ fontSize: 17 }} />
          )
        }
        onClick={onToggleStatus}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 2,
          minHeight: 42,
          flex: isMobileLayout ? undefined : "0 1 auto",
        }}
      >
        {isActive ? "Desativar" : "Reativar"}
      </Button>
    </Stack>
  );

  if (isMobileLayout) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          borderRadius: 2.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: "0 2px 12px rgba(15, 23, 42, 0.06)",
        }}
      >
        <HealthInsuranceCardFace
          coverage={coverage}
          colorIndex={colorIndex}
          layout="mobile"
          embedded
        />
        {actionsBar}
      </Box>
    );
  }

  return (
    <Stack spacing={1} sx={{ width: "100%", maxWidth: 320 }}>
      <HealthInsuranceCardFace
        coverage={coverage}
        colorIndex={colorIndex}
        layout="web"
      />
      {actionsBar}
    </Stack>
  );
}

function TabEmptyState({ tab }: { tab: InsuranceCardsTab }) {
  const message =
    tab === "active"
      ? "Nenhum cartão ativo no momento."
      : "Nenhum cartão inativo.";

  return (
    <Box
      sx={{
        py: 4,
        px: 2,
        borderRadius: 2.5,
        bgcolor: "action.hover",
        border: "1px dashed",
        borderColor: "divider",
        textAlign: "center",
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {message}
      </Typography>
    </Box>
  );
}

interface CardsListProps {
  items: Coverage[];
  layout: HealthInsuranceCardLayout;
  onEdit: (coverage: Coverage) => void;
  onToggleStatus: (coverage: Coverage) => void;
  selectedTab: InsuranceCardsTab;
}

function WebCardsGrid({
  items,
  onEdit,
  onToggleStatus,
  selectedTab,
}: Omit<CardsListProps, "layout">) {
  if (items.length === 0) {
    return <TabEmptyState tab={selectedTab} />;
  }

  return (
    <Grid container spacing={2} width="100%">
      {items.map((item, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id ?? index}>
          <InsuranceCardItem
            coverage={item}
            colorIndex={index}
            onEdit={() => onEdit(item)}
            onToggleStatus={() => onToggleStatus(item)}
            layout="web"
          />
        </Grid>
      ))}
    </Grid>
  );
}

function MobileCardsSlider({
  items,
  onEdit,
  onToggleStatus,
  selectedTab,
  onVerTudo,
}: Omit<CardsListProps, "layout"> & { onVerTudo: () => void }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const isSliderScrolling = useRef(false);

  const scrollSliderToIndex = useCallback(
    (index: number) => {
      const el = sliderRef.current;
      if (!el || items.length <= 1) return;
      isSliderScrolling.current = true;
      el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
      window.setTimeout(() => {
        isSliderScrolling.current = false;
      }, 320);
    },
    [items.length]
  );

  const handleSliderScroll = useCallback(() => {
    if (isSliderScrolling.current) return;
    const el = sliderRef.current;
    if (!el || el.clientWidth <= 0) return;
    const nextIndex = Math.round(el.scrollLeft / el.clientWidth);
    if (nextIndex !== activeSlide && nextIndex >= 0 && nextIndex < items.length) {
      setActiveSlide(nextIndex);
    }
  }, [activeSlide, items.length]);

  useEffect(() => {
    setActiveSlide(0);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
  }, [items.length, selectedTab]);

  if (items.length === 0) {
    return <TabEmptyState tab={selectedTab} />;
  }

  return (
    <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      {items.length > 1 ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Deslize para ver seus cartões
          </Typography>
          <Typography variant="caption" color="primary.main" fontWeight={800}>
            {activeSlide + 1}/{items.length}
          </Typography>
        </Stack>
      ) : null}

      <Box
        ref={sliderRef}
        onScroll={handleSliderScroll}
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: items.length > 1 ? "auto" : "hidden",
          overflowY: "hidden",
          scrollSnapType: items.length > 1 ? "x mandatory" : "none",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {items.map((item, index) => (
          <Box
            key={item.id ?? index}
            sx={{
              flex: "0 0 100%",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
              boxSizing: "border-box",
            }}
          >
            <InsuranceCardItem
              coverage={item}
              colorIndex={index}
              onEdit={() => onEdit(item)}
              onToggleStatus={() => onToggleStatus(item)}
              layout="mobile"
            />
          </Box>
        ))}
      </Box>

      {items.length > 1 ? (
        <ScheduleSliderDotsBalloon
          total={items.length}
          activeIndex={activeSlide}
          onDotClick={(index) => {
            setActiveSlide(index);
            scrollSliderToIndex(index);
          }}
          compact
        />
      ) : null}

      {items.length > 1 ? (
        <DashViewAllButton onClick={onVerTudo} count={items.length} />
      ) : null}
    </Stack>
  );
}

/** Lista de carteirinhas — abas Ativos/Inativos; slider no mobile; grade no web. */
export function InsuranceCardsView({
  coverages,
  onEdit,
  onToggleStatus,
}: InsuranceCardsViewProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedTab, setSelectedTab] = useState<InsuranceCardsTab>("active");
  const [allListOpen, setAllListOpen] = useState(false);

  const { active, inactive } = useMemo(
    () => splitCoveragesByStatus(coverages),
    [coverages]
  );

  const filteredItems = selectedTab === "active" ? active : inactive;

  /** Ao desativar/reativar o último cartão da aba, muda para a aba com conteúdo. */
  useEffect(() => {
    if (selectedTab === "active" && active.length === 0 && inactive.length > 0) {
      setSelectedTab("inactive");
    } else if (
      selectedTab === "inactive" &&
      inactive.length === 0 &&
      active.length > 0
    ) {
      setSelectedTab("active");
    }
  }, [active.length, inactive.length, selectedTab]);

  const fullListTitle =
    INSURANCE_CARDS_TABS.find((t) => t.value === selectedTab)?.label ??
    "Meus cartões";

  return (
    <Stack spacing={0} sx={{ width: "100%", minWidth: 0 }}>
      <InsuranceCardsSegmentedTabs
        value={selectedTab}
        onChange={setSelectedTab}
        activeCount={active.length}
        inactiveCount={inactive.length}
      />
      <InsuranceCardsTabHelperText tab={selectedTab} />

      {isMobileLayout ? (
        <MobileCardsSlider
          items={filteredItems}
          selectedTab={selectedTab}
          onEdit={onEdit}
          onToggleStatus={onToggleStatus}
          onVerTudo={() => setAllListOpen(true)}
        />
      ) : (
        <WebCardsGrid
          items={filteredItems}
          selectedTab={selectedTab}
          onEdit={onEdit}
          onToggleStatus={onToggleStatus}
        />
      )}

      <ScreenMobile
        open={allListOpen}
        onClose={() => setAllListOpen(false)}
        title={fullListTitle}
      >
        <Stack spacing={2} sx={{ px: 1.5, pb: 2, pt: 0.5, width: "100%" }}>
          <InsuranceCardsSegmentedTabs
            value={selectedTab}
            onChange={setSelectedTab}
            activeCount={active.length}
            inactiveCount={inactive.length}
            compact
          />
          <InsuranceCardsTabHelperText tab={selectedTab} />
          {filteredItems.length === 0 ? (
            <TabEmptyState tab={selectedTab} />
          ) : (
            filteredItems.map((item, index) => (
              <InsuranceCardItem
                key={item.id ?? index}
                coverage={item}
                colorIndex={index}
                onEdit={() => onEdit(item)}
                onToggleStatus={() => onToggleStatus(item)}
                layout="mobile"
              />
            ))
          )}
        </Stack>
      </ScreenMobile>
    </Stack>
  );
}
